import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ImmediateMeeting,
  useAcceptImmediateMeeting,
  useDeclineImmediateMeeting,
  useCancelImmediateMeeting,
} from "@/hooks/useImmediateMeetings";
import { useAuth } from "@/lib/auth";
import { format } from "date-fns";
import { Video, Clock, Check, X, ExternalLink, Loader2 } from "lucide-react";

interface ImmediateMeetingCardProps {
  meeting: ImmediateMeeting;
  mentorshipRequestId: string;
  proposerName: string;
  isProposer: boolean;
}

/**
 * ImmediateMeetingCard - Exclusively for "Zoom Now" meetings
 * This component handles ONLY immediate meetings from the immediate_meetings table.
 * Scheduled meetings with time slots are handled by ScheduledMeetingCard.
 */
export function ImmediateMeetingCard({
  meeting,
  mentorshipRequestId,
  proposerName,
  isProposer,
}: ImmediateMeetingCardProps) {
  const { user } = useAuth();
  const acceptMeeting = useAcceptImmediateMeeting();
  const declineMeeting = useDeclineImmediateMeeting();
  const cancelMeeting = useCancelImmediateMeeting();
  
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [declineNote, setDeclineNote] = useState("");

  const isPending = meeting.status === "proposed";
  const isAccepted = meeting.status === "accepted";
  const isDeclined = meeting.status === "declined";
  const isCancelled = meeting.status === "cancelled";
  const canRespond = isPending && !isProposer;
  const canCancel = (isPending || isAccepted) && !isCancelled;

  const isLoading = acceptMeeting.isPending || declineMeeting.isPending || cancelMeeting.isPending;

  const handleAccept = async () => {
    try {
      await acceptMeeting.mutateAsync({
        meetingId: meeting.id,
        threadId: meeting.thread_id,
        mentorshipRequestId,
      });
    } catch (error) {
      console.error("[ImmediateMeetingCard] Accept failed:", error);
    }
  };

  const handleDecline = async () => {
    try {
      await declineMeeting.mutateAsync({
        meetingId: meeting.id,
        threadId: meeting.thread_id,
        declineNote: declineNote.trim() || undefined,
      });
    } catch (error) {
      console.error("[ImmediateMeetingCard] Decline failed:", error);
    } finally {
      setShowDeclineDialog(false);
      setDeclineNote("");
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMeeting.mutateAsync({
        meetingId: meeting.id,
        threadId: meeting.thread_id,
        zoomMeetingId: meeting.zoom_meeting_id,
      });
    } catch (error) {
      console.error("[ImmediateMeetingCard] Cancel failed:", error);
    } finally {
      setShowCancelDialog(false);
    }
  };

  // Don't render if cancelled or declined
  if (isCancelled || isDeclined) {
    return (
      <Card className="border-l-4 border-l-muted bg-muted/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="font-medium">
              {isCancelled ? "Meeting Cancelled" : "Meeting Declined"}
            </span>
          </div>
          {isDeclined && meeting.decline_note && (
            <p className="mt-2 text-sm text-muted-foreground italic">
              Reason: "{meeting.decline_note}"
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        className={`border-l-4 relative ${
          isPending
            ? "border-l-amber-500"
            : isAccepted
            ? "border-l-green-500"
            : "border-l-muted"
        }`}
      >
        {/* X button in top right corner for cancellable meetings */}
        {canCancel && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={() => setShowCancelDialog(true)}
            disabled={isLoading}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        
        <CardContent className="p-4 pr-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Video className="h-4 w-4 text-primary" />
                <span className="font-medium">
                  {isProposer ? "You proposed" : `${proposerName} proposed`} a Zoom meeting
                </span>
                {isPending && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending
                  </Badge>
                )}
                {isAccepted && (
                  <Badge variant="outline" className="text-green-600 border-green-300">
                    <Check className="h-3 w-3 mr-1" />
                    Ready
                  </Badge>
                )}
              </div>

              <div className="text-sm text-muted-foreground space-y-1">
                <p>Duration: {meeting.duration_minutes} minutes</p>
                {meeting.availability && (
                  <p>Availability: {meeting.availability}</p>
                )}
                {meeting.note && (
                  <p className="italic">"{meeting.note}"</p>
                )}
                <p className="text-xs">
                  Proposed on {format(new Date(meeting.created_at), "MMM d, yyyy 'at' h:mm a")}
                </p>
              </div>

              {/* Zoom link when accepted */}
              {isAccepted && meeting.zoom_join_url && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                    Zoom Meeting Ready
                  </p>
                  <Button size="sm" asChild>
                    <a href={meeting.zoom_join_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Join Meeting
                    </a>
                  </Button>
                </div>
              )}
            </div>

            {/* Accept/Decline buttons for non-proposer */}
            {canRespond && (
              <div className="flex flex-col gap-2 shrink-0">
                <Button
                  size="sm"
                  onClick={handleAccept}
                  disabled={isLoading}
                >
                  {acceptMeeting.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Accept
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowDeclineDialog(true)}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4 mr-1" />
                  Decline
                </Button>
              </div>
            )}

            {/* Awaiting response badge for proposer */}
            {isPending && isProposer && (
              <Badge variant="secondary">Awaiting response</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Decline Dialog */}
      <AlertDialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              You can continue the mentorship asynchronously through messages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="decline-note">Optional note</Label>
            <Textarea
              id="decline-note"
              placeholder="e.g., 'I prefer to continue via messages for now'"
              value={declineNote}
              onChange={(e) => setDeclineNote(e.target.value)}
              className="mt-2"
              rows={2}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                await handleDecline();
              }}
              disabled={declineMeeting.isPending}
            >
              {declineMeeting.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Decline Meeting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the meeting
              {isAccepted && meeting.zoom_join_url
                ? " and remove the Zoom link"
                : ""}
              . You can propose a new meeting later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Meeting</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                await handleCancel();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelMeeting.isPending}
            >
              {cancelMeeting.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Cancel Meeting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
